import { Directive, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AmountListResponseNew,
  defaultPage,
  isSameObject,
  ListResponse,
  ListResponseNew,
  NavigationService,
  PageResponse,
  PaginatorNew,
  SelectionButton,
  TableColumn,
  TableFilter,
  TableOrder,
  utils
} from '../../../global';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {debounceTime, filter, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {SortDirection} from '@angular/material/sort';
import {ActivatedRoute} from '@angular/router';

@Directive()
export abstract class PageTableContainerDirective implements OnInit, OnDestroy {
  private readonly navigation = inject(NavigationService);
  private readonly route = inject(ActivatedRoute);

  public filterConfig: Array<TableFilter> = null;
  public selectionAttributes: Array<string> = ['id'];
  public tableConfig: Array<TableColumn> = null;
  public selectionButtons: Array<SelectionButton> = null;
  public amountSummary$ = new BehaviorSubject(null);
  public data$: Observable<Array<any>>;
  protected readonly destroy$ = new Subject();
  private readonly refresh$ = new BehaviorSubject<boolean>(true);
  private readonly _isLoading$ = new BehaviorSubject<boolean>(true);
  public readonly isLoading$ = this._isLoading$.asObservable();
  private readonly _filters$ = new BehaviorSubject<any>(null);
  public readonly filters$ = this._filters$
    .asObservable()
    .pipe(debounceTime(250));
  private readonly _filterIsOpen$ = new BehaviorSubject<boolean>(false);
  public readonly filterIsOpen$ = this._filterIsOpen$.asObservable();
  private readonly _search$ = new BehaviorSubject<string>(null);
  public readonly search$ = this._search$.asObservable();
  private readonly _order$ = new BehaviorSubject<TableOrder>(null);
  public readonly order$ = this._order$.asObservable();

  private _filters: any = null;

  public get filters(): any {
    return this._filters || {};
  }

  public set filters(value: any) {
    if (!utils.isSameObject(value, this.filters)) {
      this._page = {...this._page, page: 1};
      this.setFilters(value);
      this.refreshQueryParams(value);
      this.refresh();
    }
  }

  private _filterIsOpen = false;

  public get filterIsOpen(): boolean {
    return this._filterIsOpen;
  }

  private _search = '';

  public get search(): string {
    return this._search || null;
  }

  public set search(value: string) {
    if (typeof value === 'string') {
      this._page = {...this._page, page: 1};
      this.setSearch(value);
      this.refreshQueryParams({search: value || '', ...this.filters});
      this.refresh();
    }
  }

  private _order: TableOrder = null;

  public get order(): TableOrder {
    return this._order;
  }

  public set order(value: TableOrder) {
    const order: TableOrder =
      !!value && !!value.attribute && !!value.order
        ? {attribute: value.attribute, order: value.order}
        : {
          attribute: null,
          order: null
        };
    this.setOrder(order);
    this.refreshQueryParams({...order, ...this.filters});
    this.refresh();
  }

  private _page: PaginatorNew = defaultPage;

  private readonly _page$ = new BehaviorSubject<PaginatorNew>(this._page);
  public readonly page$ = this._page$.asObservable();

  public get page(): PaginatorNew {
    return this._page;
  }

  public set page(value: PaginatorNew) {
    this.setPage(value);
    this.refreshQueryParams({
      page: value.page,
      size: value.size,
      ...this.filters
    });

    this.refresh();
  }

  private _selection: Array<any> = [];

  public get selection(): Array<any> {
    return this._selection || [];
  }

  public set selection(value: Array<any>) {
    this._selection = value || [];
  }

  public get isSelected(): boolean {
    return !!(this._selection || []).length;
  }

  ngOnInit() {
    this.route.queryParams
      .pipe(
        debounceTime(250),
        filter(params => {
          let isChanged = false;

          const page = +params['page'] || (this.page?.page ?? 1);
          const size = +params['size'] || (this.page?.size ?? 10);
          const newPage = {
            page,
            size,
            fullLength: +this.page.fullLength || 0
          };

          if (!isSameObject(newPage, this.page)) {
            if (newPage.size !== this.page?.size || newPage.page !== this.page?.page) {
              isChanged = true;
            }
            this.page = newPage;
          }

          const search = params['search'] || '';
          if (search !== this.search) {
            this.setSearch(search);
            isChanged = true;
          }

          const orderBy = params['orderBy'];
          const orderDirection = params['orderDirection'];
          const newOrder =
            !orderBy ||
            !orderDirection ||
            !['asc', 'desc'].includes(orderDirection)
              ? null
              : {
                order: orderDirection as SortDirection,
                attribute: orderBy
              };
          if (!isSameObject(newOrder, this.order)) {
            this.setOrder(newOrder);
            isChanged = true;
          }

          const newFilters = (Object.keys(params) || [])
            .filter(
              (attribute) =>
                ![
                  'page',
                  'size',
                  'search',
                  'orderBy',
                  'orderDirection'
                ].includes(attribute)
            )
            .reduce(
              (object, attribute) => ({
                ...object,
                ...(!!params[attribute]
                  ? {
                    [attribute]: (!Array.isArray(params[attribute]) ?
                      utils.isNumber(params[attribute]) ? +params[attribute] : params[attribute] :
                      params[attribute].map(value => utils.isNumber(value) ? +value : value))
                  }
                  : {})
              }),
              {}
            );

          if (!isSameObject(newFilters, this.filters)) {
            this.setFilters(newFilters);
            isChanged = true;
          }

          return isChanged;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.data$ = this.refresh$.pipe(
      debounceTime(250),
      tap(() => this._isLoading$.next(true)),
      switchMap(() =>
        this.loadData(this.search, this.page, this.order, this.filters)
      ),
      filter((value) => !!value),
      map(
        (
          data: AmountListResponseNew<any> | PageResponse<any> | ListResponse<any> | ListResponseNew<any>
        ) => {
          const keys = (Object.keys(data || {}) || []).filter(
            (key) => key !== 'paginator'
          );
          const paginator: any = data?.paginator;
          if (paginator) {
            const receivedPaginator: PaginatorNew = {
              page: +(paginator?.page || paginator?.actualPage || 1),
              size: +(paginator?.pageSize || paginator?.size || 10),
              fullLength: +(paginator?.fullLength || 0)
            };

            const items = !!keys.length ? data[keys[0]] : [];
            if (!!data['amounts'] && (items?.length ?? 0) > 0) {
              this.amountSummary$.next({
                amounts: data['amounts'],
                allAmount: data['allAmount']
              });
            } else {
              this.amountSummary$.next(null);
            }
            this.setPage(receivedPaginator);
          }
          this._isLoading$.next(false);
          return this.formatItems(!!keys.length ? data[keys[0]] : []);
        }
      )
    );
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public formatItems(items): Array<any> {
    return items;
  }

  public openFilter() {
    this._filterIsOpen = !this._filterIsOpen;
    this._filterIsOpen$.next(this._filterIsOpen);
  }

  public setSearch(value: string) {
    this._search = value || null;
    this._search$.next(this._search);
  }

  public setOrder(value: TableOrder) {
    this._order = !!value && !!value.attribute && !!value.order ? value : null;
    this._order$.next(this._order);
  }

  public refreshData(
    _: string,
    __: PaginatorNew,
    ___: TableOrder,
    ____: any
  ): boolean {
    return true;
  }

  abstract loadData(
    search: string,
    page: PaginatorNew,
    order: TableOrder,
    filters: any
  ): any;

  protected refresh(): void {
    this.refreshData(this.search, this.page, this.order, this.filters);
  }

  protected setFilters(value) {
    this._filters = value || {};
    this._filters$.next(value);
  }

  protected resetFilters(): void {
    this._filters$.next((this._filters = {}));
    this._search$.next((this._search = ''));
    this._page$.next(
      (this._page = {page: 1, size: this._page?.size || 10, fullLength: 0})
    );
    this.order = null;
  }

  private setPage(page: PaginatorNew) {
    if (!utils.isSameObject(page, this.page)) {
      this._page = page;
      this._page$.next(page);
    }
  }

  private refreshQueryParams(filters: any = null): void {
    const page = this.page;
    const order = this.order;

    this.navigation.refreshQueryParams({
      search: this.search || null,
      orderBy: order?.attribute || null,
      orderDirection: order?.order || null,
      page: page.page || null,
      size: page.size || null,
      ...(!!filters ? filters : this.filters)
    });
  }
}
